import React, { useState } from 'react';
import { styled, StyledProps } from '@glitz/react';
import { replaceState, translate } from '@polarnopyret/scope';
import { Preset as ImagePreset, Ratio as ImageRatio, RatioPicture } from 'Shared/Image/Ratio';
import { productClickInList } from 'TrackingInformation';
import { isCompact } from 'Shared/Viewport';
import connect from 'Shared/connect';
import { ButtonTheme, SolidButton } from 'Shared/TextButtons';
import { Check16x16 } from 'Shared/Icons/Check';
import { Bag16x16 } from 'Shared/Icons/Bag';
import { Spinner16x16 } from 'Shared/Icons/Spinner';
import { Close16x16 } from 'Shared/Icons/Close';
import { Favourite20x20, FavouriteFull20x20, Favourite24x24, FavouriteFull24x24 } from 'Shared/Icons/Favorite';
import { PageType } from 'Shared/State';
import { addToCart } from 'Cart/action-creators';
import { addCartItem as checkoutAddToCart } from 'Checkout/action-creators';
import { toggleWishlistItem as toggleWishlistData } from 'WishList/action-creators';
import { wishListUrl } from 'Shared/known-urls';
import ProductListItemViewModel from 'Product/ProductListing/ProductListItemViewModel.type';
import { SetDisplayedQuickShopProducts } from 'Shared/uiReducer';
import MiniWishListViewModel from 'WishList/MiniWishListViewModel.type';
import WishListUpdateResultType from 'WishList/WishListUpdateResult.type';
import SitePages from 'AppShell/Models/SitePages.type';
import { openAccountBox } from 'SiteLayout/AccountBox/action-creators';
import Price from 'Pricing/Price';
import Link from 'Shared/Link';
import { ResponsiveLineHeight, ResponsiveSize, TextBlock, Text } from 'Shared/SharedComponents/atoms/Typography';
import CheckoutUpdateResultType from '/Checkout/Pages/Checkout/CheckoutUpdateResult.type';
import currentPageIsCheckout from 'Checkout/Pages/Checkout/current-page-is-checkout';
import currentPageIsWishlist from 'WishList/current-page-is-wishlist';
import { HideCartModal } from 'Shared/uiReducer';
import * as style from 'Shared/Style';
import { SelectableSizes } from 'Product/Sizes';
import { BgTag } from 'Product/ProductTags';

const SHOW_ADD_TO_CART = 'SHOW_ADD_TO_CART';
const SHOW_SIZES = 'SHOW_SIZES';
const SHOW_ADDED_TO_CART = 'SHOW_ADDED_TO_CART';
const SHOW_LOADING = 'SHOW_LOADING';
const imageRatio = ImageRatio.ThreeToFour;

export const ANIMATION_DURATION = 250;
export const HEIGHT = 120;
export const COMPACT_HEIGHT = 64;

export type RequiredPropType = {
  product: ProductListItemViewModel;
  className?: string;
  noRating?: boolean;
  listIndex: number;
  smallIcons?: boolean;
  showWeatherPro?: boolean;
  quickAddToCart?: boolean;
  disableImageSwap?: boolean;
};

type ConnectedPropType = {
  currentBreakpoint: number;
  currentPage: PageType;
  displayCartModal: boolean | undefined;
  shopAtStore: boolean;
  shopAtStoreLabel: string[];
  pages: SitePages;
  loggedIn: boolean;
  wishlist: MiniWishListViewModel;
  culture: string;
  currency: string;
};

type AddToCartType = {
  type: string;
  sizeName: string;
  sizeCode: string;
};

export type PropType = RequiredPropType;

const ProductCardWrap = styled.div({
});

export const ProductLink = styled(Link, {
  color: 'inherit',
  textDecoration: 'none',
  display: 'block',
  position: 'relative',
});

const TagWrap = styled.div({
  display: 'flex',
  gap: '6px',
  minHeight: '12px',
});

const Tag = styled(Text, {
  marginRight: '6px',
});

const ProductMarkingsWrap = styled.div({
  position: 'absolute',
  top: '12px',
  left: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  ['@media ' + style.mediaMinLarge]: {
    top: '16px',
    left: '16px',
    fontSize: '14px',
    gap: '8px',
  },
});

const ProductMarking = styled.span({
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'uppercase',
  lineHeight: '100%',
  color: style.colors.black.default,
  pointerEvents: 'none',
  ['@media ' + style.mediaMinLarge]: {
    fontSize: '14px',
  },
});

const FavoriteWrap = styled.div({
  position: 'absolute',
  pointerEvents: 'none',
  ['@media ' + style.mediaUpToLarge]: {
    top: '8px',
    right: '8px',
  },
  ['@media ' + style.mediaMinLarge]: {
    top: '16px',
    right: '16px',
  },
});

const FavoriteButton = styled(SolidButton, {
  background: {
    color: 'transparent',
    image: 'none'
  },
  pointerEvents: 'all',
  color: style.colors.black.ink,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: { xy: { width: 0 } },
  cursor: 'pointer',
  width: '22px',
  height: '22px',
  padding: { xy: 0 },
  lineHeight: 1,
  ['@media ' + style.mediaMinLarge]: {
    width: '26px',
    height: '26px',
  },
})

const DiscountBox = styled.div({
  position: 'absolute',
  pointerEvents: 'none',
  ['@media ' + style.mediaUpToLarge]: {
    bottom: '12px',
    left: '12px',
  },
  ['@media ' + style.mediaMinLarge]: {
    bottom: '16px',
    left: '16px',
  },
});

const ProductLinkWrap = styled.div({
  padding: { top: 8 },
  display: 'block',
  fontSize: 14,
  lineHeight: '100%',
  fontWeight: 'bold',
  color: style.colors.black.default,
});

const ProductLinkWrapInner = styled.div({
  display: 'flex',
  ['@media ' + style.mediaUpToLarge]: {
    flexDirection: 'column',
    gap: '4px',
  },
});

const ProductLinkTextWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  ['@media ' + style.mediaMinLarge]: {
    flexGrow: 1,
  },
});

const ProductOnelineText = styled(TextBlock, {
  textTransform: 'initial',
  fontWeight: 450,
  minHeight: `calc(1.2 * 12)px`,
  ['@media ' + style.mediaMinLarge]: {
    fontWeight: 500,
    minHeight: `calc(1.3 * 14)px`,
  },
});

const ProductSize = styled(TextBlock, {
  textTransform: 'initial',
  fontSize: '14px',
  color: style.colors.grey.dark,
});

const QuickAddToCartWrap = styled.div({
  position: 'absolute',
  zIndex: 5,
  boxSizing: 'border-box',
  bottom: '6px',
  width: '100%',
  pointerEvents: 'none',
  display: 'flex',
  justifyContent: 'right',
  paddingLeft: '6px',
  paddingRight: '6px',

  ['@media ' + style.mediaMinLarge]: {
    bottom: '12px',
    paddingLeft: '12px',
    paddingRight: '12px',
    transition: {
      property: 'opacity',
      duration: '.2s',
      timingFunction: 'ease'
    },
  },
});

const QuickShopButton = styled(SolidButton, {
  background: {
    color: style.colors.backgrounds.white,
    image: 'none'
  },
  color: style.colors.black.ink,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 8px rgba(0, 0, 0, .12)',
  border: { xy: { width: 0 } },
  cursor: 'pointer',
  width: '36px',
  height: '36px',
  padding: { xy: 0 },
  lineHeight: 1,
})

const SizeSelectorWrap = styled.div({
  pointerEvents: 'all',
  width: '100%'
});

const SizeSelectorClose = styled.div({
  display: 'flex',
  justifyContent: 'right',
  marginRight: '2px',
  marginBottom: '4px',
  ['@media ' + style.mediaMinLarge]: {
    marginRight: '6px',
    marginBottom: '6px',
  },
});

const ImageWrap = styled.div({
  position: 'relative'
})

const PrimaryImage = styled(RatioPicture, {
  borderTopRightRadius: '8px',
  borderTopLeftRadius: '8px',
});

const SecondaryImage = styled(RatioPicture, {
  borderTopRightRadius: '8px',
  borderTopLeftRadius: '8px',
  position: 'absolute',
  width: '100%',
  height: '100%',
  transform: 'translateY(-100%)',
  transition: {
    property: 'opacity',
    duration: '0.3s',
  },
  opacity: 0,
});

const CardOverlay = styled.div({
  backgroundColor: style.colors.rgbaFilters.blue,
  position: 'absolute',
  zIndex: 1,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderTopRightRadius: '8px',
  borderTopLeftRadius: '8px',
});

const CardOverlayLabel = styled(Text, {
  color: style.colors.monochrome.white,
  textAlign: 'center',
  lineHeight: '110%',
  paddingLeft: '16px',
  paddingRight: '16px',
  ['@media ' + style.mediaMinLarge]: {
    paddingLeft: '32px',
    paddingRight: '32px',
  },
});

const SwipeContainer = styled.div({
  display: 'block',
});

const SwipeStrip = styled.div({
  display: 'flex',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollSnapType: 'x mandatory',
  // optional scrollbar styling for Firefox
  scrollbarWidth: 'none',
  borderTopRightRadius: '8px',
  borderTopLeftRadius: '8px',
});

const SwipeItem = styled.div({
  minWidth: '100%',
  scrollSnapAlign: 'start'
});

const SwipeImage = styled(RatioPicture, {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const ProductCard = styled(
  connect(
    (state) => ({
      wishlist: state.wishlist,
      culture: state.appShellData.culture,
      currency: state.appShellData.market.currency,
      cartValue: state.appShellData.cart.totalPrice,
      pages: state.appShellData.pages,
      shopAtStore: state.appShellData.market.shopAtStore,
      shopAtStoreLabel: state.appShellData.market.shopAtStoreLabel,
      loggedIn: state.currentUser?.isLoggedIn ?? false,
      currentBreakpoint: state.currentBreakpoint,
      currentPage: state.currentPage,
      displayCartModal: state.UI?.displayCartModal,
    }),
    (dispatch) => ({
      toggleWishlistItem(isFavorited: boolean, code: string, colorProductCode: string, currentCategory: string) {
        return dispatch(toggleWishlistData(isFavorited, code, colorProductCode, currentCategory));
      },
      addToCart(
        currentPage: PageType,
        code: string,
        quantity: number,
        ticket: string,
        currentCategory: string,
        colorProductCode: string,
      ) {
        if (currentPageIsCheckout(currentPage)) {
          return dispatch(checkoutAddToCart(code, quantity, ticket, 'Kassasida', currentCategory, colorProductCode));
        } else {
          return dispatch(
            addToCart(code, quantity, ticket, 'Produktlista', currentCategory, colorProductCode, false, true),
          );
        }
      },
      setDisplayedQuickShopProducts: (products?: ProductListItemViewModel[]) =>
        dispatch(SetDisplayedQuickShopProducts(products)),
      openAccountBox() {
        dispatch(openAccountBox());
      },
      hideCartModal: () => dispatch(HideCartModal()),
    }),
  )(
    (
      props: PropType &
        ConnectedPropType &
        StyledProps & {
          cartValue: number;
          toggleWishlistItem: (
            isFavorited: boolean,
            code: string,
            colorProductCode: string,
            currentCategory: string,
          ) => Promise<WishListUpdateResultType>;
          addToCart: (
            currentPage: PageType,
            code: string,
            quantity: number,
            ticket: string,
            currentCategory: string,
            colorProductCode: string,
          ) => Promise<CheckoutUpdateResultType>;
          setDisplayedQuickShopProducts: (products?: ProductListItemViewModel[] | null) => void;
          openAccountBox: () => void;
          hideCartModal: () => void;
        },
    ) => {
      const {
        product,
        currency,
        cartValue,
        quickAddToCart,
        addToCart,
        setDisplayedQuickShopProducts,
        toggleWishlistItem,
        openAccountBox,
        hideCartModal,
        loggedIn,
        currentPage,
        disableImageSwap = false
      } = props;

      const onTracking = () => {
        productClickInList(product, product.categoryName, props.listIndex + 1, currency, cartValue);
      };
      const isWishListPage = currentPageIsWishlist(currentPage);
      const [isFavorited, setFavourite] = useState(props.wishlist.items?.some((p) => product.code === p));
      const [quickAddStep, setQuickAddStep] = useState({ type: SHOW_ADD_TO_CART } as AddToCartType);

      const isMobile = isCompact(props.currentBreakpoint);
      const multipleImages = !isMobile && product.imageUrls?.length > 1;
      const Favorite =
        isFavorited || isWishListPage
          ? isMobile
            ? FavouriteFull20x20
            : FavouriteFull24x24
          : isMobile
            ? Favourite20x20
            : Favourite24x24;
      const enabledImageHover = !isMobile || isWishListPage || multipleImages;
      const displayCardOverlay =
        product.comingSoon || (props.shopAtStore && props.shopAtStoreLabel && product.outOfStock);
      const cardOverLayLabel = product.comingSoon
        ? [translate('/site/product/comingsoonlist')]
        : props.shopAtStoreLabel;

      const enabledQuickAdd = !displayCardOverlay && quickAddToCart;

      const addSizeToCart = async (code: string, name: string | React.ReactNode) => {
        setQuickAddStep({ type: SHOW_LOADING } as AddToCartType);

        const b = await addToCart(currentPage, code, 1, product.ticket, product.categoryName, product.code);
        if (b.success) {
          setQuickAddStep({ type: SHOW_ADDED_TO_CART, sizeCode: code, sizeName: name } as AddToCartType);
          setTimeout(() => {
            setQuickAddStep({ type: SHOW_ADD_TO_CART } as AddToCartType);
          }, 2000);
          return Promise.resolve();
        } else {
          setQuickAddStep({ type: SHOW_ADD_TO_CART } as AddToCartType);
          return Promise.reject(null);
        }
      };

      const toggleWishlist = async (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        isFavorited: boolean,
        code: string,
      ) => {
        const b = await toggleWishlistItem(isFavorited, code, product.code, product.categoryName);
        if (b?.statusCode === 200) {
          const value = !isFavorited;
          if (isWishListPage && !value) {
            replaceState(wishListUrl());
          } else {
            setFavourite(value);
          }
        }
      };

      const productTags = (
        <TagWrap>
          {product.tags && <div>
            {product.tags?.map((tag, index) => {
              return (
                <Tag
                  key={index}
                  upperCase
                  fontSize={ResponsiveSize.D12_M12}
                  css={{
                    color: tag.color,
                  }}
                >
                  {tag.tag}
                </Tag>
              );
            })}
          </div>}
        </TagWrap>
      );

      const handleSwipe = (e) => {
        const container = e.target;
        const slideWidth = container.clientWidth;
        if (!slideWidth) return;

        const index = Math.round(container.scrollLeft / slideWidth);
        //setActiveIndex(index);
      }

      const hoverProduct = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, flag: boolean) => {
        const opacity = flag ? '1' : '0';
        const elems = event.currentTarget.querySelectorAll('.p-opac');
        elems.forEach((el) => {
          (el as HTMLElement).style.opacity = opacity;
        });
      };

      let hasDiscount = product.price.listPrice > 0 && product.price.sellingPrice < product.price.listPrice;

      // if (typeof currentPriceLow === 'number' && typeof currentPriceHigh === 'number') {
      //   hasDiscount = false;

      //   if (product.price.originalPriceLow > 0 && product.price.originalPriceHigh > 0) {
      //     if (currentPriceLow < originalPriceLow || currentPriceHigh < originalPriceHigh) {
      //       hasDiscount = true;
      //     }
      //   }

      //   priceRender = formatPrice(currentPriceLow, currency, culture, ([LanguageCodes.Finnish, LanguageCodes.German] as String[]).includes(culture)) + " - " + formatPrice(currentPriceHigh, currency, culture);
      //   priceRenderOriginal = formatPrice(originalPriceLow, currency, culture, ([LanguageCodes.Finnish, LanguageCodes.German] as String[]).includes(culture)) + " - " + formatPrice(originalPriceHigh, currency, culture);
      //   priceRenderCompare = comparePrice > 0 && formatPrice(comparePriceLow, currency, culture, ([LanguageCodes.Finnish, LanguageCodes.German] as String[]).includes(culture)) + " - " + formatPrice(comparePriceHigh, currency, culture);
      // }

      let discountPercent = 0;
      if (hasDiscount) {
        discountPercent = Math.round(((product.price.listPrice - product.price.sellingPrice) / product.price.listPrice) * 100);
      }

      return (
        <ProductCardWrap
          className="product-card"
          // css={props.compose({
          //   overflow: 'visible',
          // })}
          onMouseEnter={(e) => (enabledImageHover ? hoverProduct(e, true) : null)}
          onMouseLeave={(e) => (enabledImageHover ? hoverProduct(e, false) : null)}
        >
          <ProductLink
            to={product.url}
            onPageLoaded={() => {
              setDisplayedQuickShopProducts(null);
            }}
            linkIdentifier={product.ticket}
            onClick={() => {
              onTracking();
              if (props.displayCartModal) {
                hideCartModal();
              }
            }}
            aria-label={product.displayName}
          >
            <ImageWrap>
              {displayCardOverlay && (
                <CardOverlay>
                  {cardOverLayLabel?.map((label, index) => (
                    <CardOverlayLabel key={index} fontSize={ResponsiveSize.D32_M16} upperCase>
                      {label}
                    </CardOverlayLabel>
                  ))}
                </CardOverlay>
              )}
              {isMobile && !disableImageSwap && (
                <SwipeContainer>
                  <SwipeStrip onScroll={handleSwipe}>
                    {product.imageUrls?.map((src, idx) => {
                      return (
                        <SwipeItem key={idx}>
                          <SwipeImage
                            src={src}
                            title={product.displayName}
                            alt={product.displayName}
                            ratio={imageRatio}
                            preset={ImagePreset.ListItem}
                          />
                        </SwipeItem>
                      )
                    })}
                  </SwipeStrip>
                </SwipeContainer>
              )}
              {(!isMobile || disableImageSwap) && <>
                <PrimaryImage
                  src={product.imageUrls?.[0]}
                  title={product.displayName}
                  alt={product.displayName}
                  ratio={imageRatio}
                  preset={ImagePreset.ListItem}
                />
                {multipleImages && (
                  <SecondaryImage
                    wrapperClassName="p-opac"
                    src={product.imageUrls?.[1]}
                    title={product.displayName}
                    alt={product.displayName}
                    ratio={imageRatio}
                    preset={ImagePreset.ListItem}
                    wrapperStyle={{
                      transitionProperty: 'opacity',
                      transitionDuration: '0.3s',
                      opacity: 0,
                    }}
                  />
                )}
              </>}
              {!enabledQuickAdd && (
                <FavoriteWrap onClick={(e) => e.preventDefault()}>
                  <FavoriteButton
                    asButton
                    theme={ButtonTheme.Empty}
                    title={isFavorited ? translate('/site/wishlist/remove') : translate('/site/wishlist/add')}
                    aria-label={isFavorited ? translate('/site/wishlist/remove') : translate('/site/wishlist/add')}
                    onClick={(e) => {
                      if (loggedIn) {
                        toggleWishlist(e, isFavorited, product.preSelectedSizeCode);
                      } else {
                        return openAccountBox();
                      }
                    }}
                  >
                    <Favorite
                      css={{
                        color: 'black',
                        fill: isFavorited ? 'black' : 'none',
                      }}
                    />
                  </FavoriteButton>
                </FavoriteWrap>
              )}
              {product.markings && product.markings.length > 0 && (
                <ProductMarkingsWrap>
                  {product.markings?.map((marking, index) => (
                    <ProductMarking key={index}>{marking}</ProductMarking>
                  ))}
                </ProductMarkingsWrap>
              )}
              {discountPercent > 0 && quickAddStep.type !== SHOW_SIZES && (
                <DiscountBox>
                  <BgTag
                    css={{
                      color: style.colors.functions.sale,
                      background: {
                        color: style.colors.monochrome.white,
                        image: 'none'
                      },
                    }}
                  >
                    -{discountPercent}%
                  </BgTag>
                </DiscountBox>
              )}

              {enabledQuickAdd && (
                <QuickAddToCartWrap onClick={(e) => e.preventDefault()}>
                  {quickAddStep.type !== SHOW_SIZES && (
                    <QuickShopButton
                      asButton
                      title={translate('/site/cart/add')}
                      aria-label={translate('/site/cart/add')}
                      onClick={(e) => {
                        quickAddStep.type === SHOW_ADD_TO_CART ? setQuickAddStep({ type: SHOW_SIZES } as AddToCartType) : false;
                      }}
                      css={{
                        pointerEvents: quickAddStep.type === SHOW_ADD_TO_CART ? 'all' : 'none',
                      }}
                      theme={ButtonTheme.Empty}
                    >
                      {quickAddStep.type === SHOW_ADD_TO_CART && <Bag16x16 />}
                      {quickAddStep.type === SHOW_LOADING && <Spinner16x16 />}
                      {quickAddStep.type === SHOW_ADDED_TO_CART && <Check16x16 />}
                    </QuickShopButton>
                  )}

                  {quickAddStep.type === SHOW_SIZES && (
                    <SizeSelectorWrap>
                      <SizeSelectorClose><Close16x16 onClick={() => setQuickAddStep({ type: SHOW_ADD_TO_CART } as AddToCartType)} /></SizeSelectorClose>
                      {/* <SizeLabel>{translate('/site/cart/size')}</SizeLabel> */}
                      <SelectableSizes
                        selectedSizeCode=""
                        sizes={product.sizes}
                        notifyMe={false}
                        placeholder={translate('/site/cart/size')}
                        setSelectedSize={(size, sizeName) => {
                          addSizeToCart(size.code, sizeName);
                        }}
                      />
                    </SizeSelectorWrap>
                  )}

                  {/* {quickAddStep.type === SHOW_ADD_TO_CART && (
                    <QuickAddButton
                      asButton
                      title={translate('/site/cart/add')}
                      onClick={(e) => {
                        setQuickAddStep({ type: SHOW_SIZES } as AddToCartType);
                      }}
                      css={{
                        pointerEvents: quickAddStep.type === SHOW_ADD_TO_CART ? 'all' : 'none',
                      }}
                      theme={ButtonTheme.Empty}
                      aria-label={translate('/site/cart/add')}
                    >{<Bag16x16 />}</QuickAddButton>
                  )} */}
                  {/* {quickAddStep.type === SHOW_LOADING && (
                    <QuickAddSpinner
                      css={{ pointerEvents: 'none' }}
                      theme={ButtonTheme.Empty}
                      aria-label={translate('/site/cart/add')}
                    ><Spinner16x16 /></QuickAddSpinner>
                  )}

                  {quickAddStep.type === SHOW_ADDED_TO_CART && (
                    <QuickAddComplete
                      onClick={() => { }}
                      css={{ pointerEvents: 'none' }}
                      theme={ButtonTheme.Empty}
                      aria-label={translate('/site/cart/add')}
                    ><Check /></QuickAddComplete>
                  )} */}

                </QuickAddToCartWrap>
              )}
            </ImageWrap>

            {/* {!isMobile && (
                <RatioPicture
                  src={product.imageUrls?.[0]}
                  title={product.displayName}
                  alt={product.displayName}
                  ratio={imageRatio}
                  preset={ImagePreset.ListItem}
                />)}
              {multipleImages && (
                <SecondaryImage
                  wrapperClassName="p-opac"
                  src={product.imageUrls?.[1]}
                  title={product.displayName}
                  alt={product.displayName}
                  ratio={imageRatio}
                  preset={ImagePreset.ListItem}
                  wrapperStyle={{
                    transitionProperty: 'opacity',
                    transitionDuration: '0.3s',
                    opacity: 0,
                  }}
                />
              )} */}


            <ProductLinkWrap>
              <ProductLinkWrapInner>
                <ProductLinkTextWrap>
                  <TextBlock fontSize={ResponsiveSize.D14_M14} lineHeight={ResponsiveLineHeight.D100_M100} upperCase>
                    {product.displayName}
                  </TextBlock>
                  <ProductOnelineText fontSize={ResponsiveSize.D14_M12} lineHeight={ResponsiveLineHeight.D130_M120}>
                    {product.oneLineText}
                  </ProductOnelineText>
                  {product.sizeRange && product.sizeRange !== '' && <ProductSize>
                    {translate('/site/shared/sizeshort')}: {product.sizeRange}
                  </ProductSize>}
                  {!isMobile && productTags}
                </ProductLinkTextWrap>
                <Price microData {...product.price} vertical={!isMobile} fontSize={14} fontSizePrevPrice={12} />
                {isMobile && productTags}
              </ProductLinkWrapInner>
            </ProductLinkWrap>
          </ProductLink >
        </ProductCardWrap >
      );
    },
  ),
);

export default ProductCard;
